import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Portal,
  Snackbar,
  Stack,
  Typography
} from '@mui/material';
import {
  Close as CloseIcon,
  DoneAll as DoneAllIcon,
  OpenInNew as OpenInNewIcon,
  WarningAmber as WarningAmberIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  isCriticalNotification,
  markNotificationAsRead
} from '../../redux/slices/notificationsSlice';
import { selectCurrentUser } from '../../redux/slices/authSlice';
import tabCoordinator from '../../services/tabCoordinator';
import useFullscreenElement from '../../hooks/useFullscreenElement';

// Ungelesene kritische Benachrichtigungen zusätzlich als Overlay anzeigen —
// analog zur Anruf-Anzeige (IncomingCallNotifier), damit der Disponent sie
// auch ohne Blick auf die Glocke nicht verpasst. Der Firestore-Listener wird
// bereits von der Navbar gestartet (subscribeToNotifications); hier wird nur
// derselbe Redux-State gelesen — ein zweites Subscribe würde den bestehenden
// Callback abräumen.
//
// Nur Meldungen der letzten 15 Minuten: ältere ungelesene Kritische bleiben
// in der Glocke sichtbar, sollen aber nach Login/Reload nicht dauerhaft den
// Bildschirm belegen.
const CRITICAL_OVERLAY_MAX_AGE_MS = 15 * 60 * 1000;

const MAX_DISMISSED_NOTIFICATIONS = 100;

const dismissedStorageKey = (userId) =>
  `nextcab.notifications.dismissedCritical.${userId || 'anonymous'}`;

// Ausgeblendete Meldungen ueberdauern jetzt einen Reload: Vorher lagen sie nur
// im React-State und kamen nach jedem Neuladen als Overlay zurueck.
const loadDismissedIds = (userId) => {
  if (!userId) return new Set();
  try {
    const stored = JSON.parse(localStorage.getItem(dismissedStorageKey(userId)) || '[]');
    return new Set(Array.isArray(stored) ? stored.filter((id) => typeof id === 'string') : []);
  } catch (_) {
    return new Set();
  }
};

const saveDismissedIds = (userId, ids) => {
  if (!userId) return;
  try {
    localStorage.setItem(
      dismissedStorageKey(userId),
      JSON.stringify(Array.from(ids).slice(-MAX_DISMISSED_NOTIFICATIONS)),
    );
  } catch (_) {
    // Innerhalb der Sitzung bleibt die Meldung trotzdem ausgeblendet.
  }
};

const toMillis = (value) => {
  try {
    const date = value?.toDate ? value.toDate() : new Date(value);
    const ms = date.getTime();
    return Number.isFinite(ms) ? ms : null;
  } catch (_) {
    return null;
  }
};

const CriticalNotificationNotifier = () => {
  const navigate = useNavigate();
  const fullscreenElement = useFullscreenElement();
  const dispatch = useDispatch();
  const { notifications } = useSelector((state) => state.notifications);
  const user = useSelector(selectCurrentUser);
  const userId = user?.uid || '';
  // Ausgeblendete IDs: Meldung bleibt ungelesen in der Glocke, das Overlay
  // verschwindet aber — in allen Fenstern und über einen Reload hinweg.
  const [dismissedIds, setDismissedIds] = useState(() => loadDismissedIds(''));

  useEffect(() => {
    setDismissedIds(loadDismissedIds(userId));
  }, [userId]);

  // Ausblenden aus einem anderen Portal-Fenster übernehmen.
  useEffect(() => tabCoordinator.onDismiss('notification', (notificationId) => {
    setDismissedIds((current) => {
      if (current.has(notificationId)) return current;
      const next = new Set(current);
      next.add(notificationId);
      saveDismissedIds(userId, next);
      return next;
    });
  }), [userId]);

  const pending = useMemo(() => {
    const cutoff = Date.now() - CRITICAL_OVERLAY_MAX_AGE_MS;
    return (notifications || [])
      .filter((n) => !n.read && isCriticalNotification(n) && !dismissedIds.has(n.id))
      .filter((n) => {
        const createdMs = toMillis(n.createdAt);
        return createdMs !== null && createdMs >= cutoff;
      })
      .sort((a, b) => (toMillis(b.createdAt) ?? 0) - (toMillis(a.createdAt) ?? 0));
  }, [notifications, dismissedIds]);

  const active = pending[0] || null;

  const dismiss = () => {
    if (!active) return;
    const notificationId = active.id;
    setDismissedIds((current) => {
      const next = new Set(current);
      next.add(notificationId);
      saveDismissedIds(userId, next);
      return next;
    });
    tabCoordinator.broadcastDismiss('notification', notificationId);
  };

  const markRead = () => {
    if (!active) return;
    dispatch(markNotificationAsRead(active.id));
  };

  const openTarget = () => {
    if (!active) return;
    const rideId = active.data?.rideId || active.data?.ride_id;
    const url = active.data?.url || (rideId ? `/fahrten/${rideId}` : null);
    if (url) navigate(url);
    markRead();
  };

  // Im Vollbild der LiveMap in das Vollbild-Element rendern (wie der
  // IncomingCallNotifier), sonst bliebe die Meldung unsichtbar.
  return (
    <Portal container={fullscreenElement}>
      <Snackbar
        open={Boolean(active)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{ mt: 8, maxWidth: 480 }}
      >
        <Card elevation={8} sx={{ width: '100%', borderLeft: 5, borderColor: 'error.main' }}>
          {active && (
            <>
              <CardContent>
                <Stack direction="row" spacing={1.5} alignItems="flex-start">
                  <WarningAmberIcon color="error" sx={{ mt: 0.5 }} />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="overline" color="error" fontWeight="bold">
                      🚨 Kritische Meldung
                    </Typography>
                    <Typography variant="h6">{active.title || 'Kritische Meldung'}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {active.body || ''}
                    </Typography>
                    {pending.length > 1 && (
                      <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
                        +{pending.length - 1} weitere kritische Meldung(en)
                      </Typography>
                    )}
                  </Box>
                </Stack>
              </CardContent>
              <CardActions sx={{ px: 2, pb: 2 }}>
                {(active.data?.url || active.data?.rideId || active.data?.ride_id) && (
                  <Button variant="contained" color="error" startIcon={<OpenInNewIcon />} onClick={openTarget}>
                    Öffnen
                  </Button>
                )}
                <Button color="inherit" startIcon={<DoneAllIcon />} onClick={markRead}>
                  Gelesen
                </Button>
                <Button color="inherit" startIcon={<CloseIcon />} onClick={dismiss}>
                  Ausblenden
                </Button>
              </CardActions>
            </>
          )}
        </Card>
      </Snackbar>
    </Portal>
  );
};

export default CriticalNotificationNotifier;
